const AUTH_DB_NAME = 'firebaseLocalStorageDb';
const AUTH_DB_VERSION = 1;
const AUTH_STORE = 'firebaseLocalStorage';
const AUTH_KEY_PATH = 'fbase_key';

export type PersistedAuthRecord = {
  uid?: string;
  isAnonymous?: boolean;
  email?: string | null;
};

type SessionUser = {
  isAnonymous: boolean;
};

export function authUserPersistenceKey(apiKey: string, appName: string): string {
  return `firebase:authUser:${apiKey}:${appName}`;
}

export function isAnonymousPersistedAccount(user: PersistedAuthRecord | null | undefined): boolean {
  if (!user?.uid) return false;
  if (user.isAnonymous === true) return true;
  if (user.isAnonymous === false) return false;
  return !user.email;
}

/**
 * A signed-in account replaces anonymous records. Distinct signed-in accounts
 * are left where they are so one real session does not clobber another.
 * Empty slots stay empty; Firebase skips those and reads the next store.
 */
export function applyAccountPriority<T extends PersistedAuthRecord>(
  records: Array<T | null>,
): Array<T | null> {
  const signedIn = records.find((record) => record?.uid && !isAnonymousPersistedAccount(record)) ?? null;
  if (!signedIn) return records;

  return records.map((record) => (
    record?.uid && isAnonymousPersistedAccount(record) ? signedIn : record
  ));
}

export function shouldBlockAnonymousTakeover(
  current: { isAnonymous: boolean } | null,
  next: { isAnonymous: boolean } | null,
): boolean {
  return Boolean(current && !current.isAnonymous && next?.isAnonymous);
}

export async function authenticateWithoutReplacingSignedInUser<T extends SessionUser>(options: {
  whenReady: Promise<void>;
  currentUser: () => T | null;
  signInAnonymously: () => Promise<T>;
}): Promise<T> {
  await options.whenReady;

  const existing = options.currentUser();
  if (existing) return existing;

  try {
    const signedIn = await options.signInAnonymously();
    const current = options.currentUser();
    if (current && !current.isAnonymous) return current;
    return signedIn;
  } catch (error) {
    const current = options.currentUser();
    if (current && !current.isAnonymous) return current;
    throw error;
  }
}

function asPersistedUser(value: unknown): PersistedAuthRecord | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as PersistedAuthRecord;
  if (typeof record.uid !== 'string' || !record.uid) return null;
  return record;
}

function readWebStorage(storage: Storage | undefined, key: string): PersistedAuthRecord | null {
  try {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    return asPersistedUser(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeWebStorage(storage: Storage | undefined, key: string, value: PersistedAuthRecord) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode and full storage can reject the write. Auth still has the other copy.
  }
}

/**
 * Firebase keeps the same auth user key in IndexedDB, localStorage, and
 * sessionStorage, and the first store that has a user wins. If a listener
 * session and an admin session landed in different stores, copy the admin
 * record over the anonymous one before Auth initializes.
 */
export function preferSignedInPersistedAccount(apiKey: string, appName: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return Promise.resolve();

  const key = authUserPersistenceKey(apiKey, appName);

  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(AUTH_DB_NAME, AUTH_DB_VERSION);
    } catch {
      done();
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(AUTH_STORE)) {
        db.createObjectStore(AUTH_STORE, { keyPath: AUTH_KEY_PATH });
      }
    };
    request.onerror = () => done();
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(AUTH_STORE)) {
        db.close();
        done();
        return;
      }

      let tx: IDBTransaction;
      try {
        tx = db.transaction(AUTH_STORE, 'readwrite');
      } catch {
        db.close();
        done();
        return;
      }

      const finish = () => {
        db.close();
        done();
      };
      tx.oncomplete = finish;
      tx.onerror = finish;
      tx.onabort = finish;

      const getRequest = tx.objectStore(AUTH_STORE).get(key);
      getRequest.onsuccess = () => {
        const indexedDbUser = asPersistedUser(getRequest.result?.value);
        const localUser = readWebStorage(window.localStorage, key);
        const sessionUser = readWebStorage(window.sessionStorage, key);
        const [nextIndexedDb, nextLocal, nextSession] = applyAccountPriority([
          indexedDbUser,
          localUser,
          sessionUser,
        ]);

        try {
          if (nextIndexedDb && nextIndexedDb !== indexedDbUser) {
            tx.objectStore(AUTH_STORE).put({ [AUTH_KEY_PATH]: key, value: nextIndexedDb });
          }
          if (nextLocal && nextLocal !== localUser) {
            writeWebStorage(window.localStorage, key, nextLocal);
          }
          if (nextSession && nextSession !== sessionUser) {
            writeWebStorage(window.sessionStorage, key, nextSession);
          }
        } catch {
          // Leave the transaction to complete with whatever writes succeeded.
        }
      };
    };
  });
}
